import {Injectable} from '@angular/core';
import {GATEWAY_STATUS} from '@constants/gateway/gateway-status';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {GATEWAY_STREAMING_TYPE} from '@constants/gateway/gateway-streaming-type';
import {GATEWAY_UPDATE_STATUS} from '@constants/gateway/gateway-update-status';
import {Gateway} from '@models/gateway/gateway';
import {CONNECTION_STATE} from '@models/gateway/connection/connection-state.constant';
import {UPDATE_STATE} from '@models/gateway/update/update-state.constant';

@Injectable({
  providedIn: 'root'
})
export class GatewayStatusService {
  private NON_CONTINUOUS_STREAMING_THRESHOLD_MINUTES: number = 5;

  constructor() {
  }

  public determineGatewayStatusByType(gateway: Gateway): GATEWAY_STATUS {
    const fallbackStatus: GATEWAY_STATUS = GATEWAY_STATUS.OFFLINE_OR_UNKNOWN;

    if (!gateway || !gateway.cloudplugType) {
      return fallbackStatus;
    }
    let status: GATEWAY_STATUS;
    switch (gateway.cloudplugType) {
      case GATEWAY_TYPE.CPONE: {
        const streamingType: GATEWAY_STREAMING_TYPE = this.determineStreamingType(gateway);
        if (streamingType === GATEWAY_STREAMING_TYPE.NON_CONTINUOUS) {
          status = this.determineStateNonContinuousStreaming(gateway);
        } else if (streamingType === GATEWAY_STREAMING_TYPE.CONTINUOUS) {
          status = this.determineStateContinuousStreaming(gateway);
        } else {
          status = fallbackStatus;
        }
        break;
      }
      case GATEWAY_TYPE.CPEDGE: {
        status = this.determineStateContinuousStreaming(gateway);
        break;
      }
      case GATEWAY_TYPE.GENERIC: {
        status = this.determineStateContinuousStreaming(gateway);
        break;
      }
      case GATEWAY_TYPE.CPLIGHT: {
        status = this.determineStateContinuousStreaming(gateway);
        break;
      }
      case GATEWAY_TYPE.ARGOS_GATEWAY: {
        status = this.determineStateContinuousStreaming(gateway);
        break;
      }
      default: {
        status = fallbackStatus;
        break;
      }
    }
    return status;

  }

  public determineGatewayUpdateState(gateway: Gateway): GATEWAY_UPDATE_STATUS {
    let result: GATEWAY_UPDATE_STATUS = GATEWAY_UPDATE_STATUS.NONE;

    if (!gateway.updateStatus || !gateway.updateStatus.state) {
      return result;
    }
    const updateState: UPDATE_STATE = gateway.updateStatus.state;
    //SUCCESS,AVAILABLE, PROGRESS, PENDING, ERROR, NONE
    switch (updateState) {
      case UPDATE_STATE.SUCCESS:
        result = GATEWAY_UPDATE_STATUS.SUCCESS;
        break;
      case UPDATE_STATE.AVAILABLE:
        result = GATEWAY_UPDATE_STATUS.AVAILABLE;
        break;
      case UPDATE_STATE.PROGRESS:
        result = GATEWAY_UPDATE_STATUS.PROGRESS;
        break;
      case UPDATE_STATE.PENDING:
        result = GATEWAY_UPDATE_STATUS.PENDING;
        break;
      case UPDATE_STATE.ERROR:
        result = GATEWAY_UPDATE_STATUS.ERROR;
        break;
      default:
        result = GATEWAY_UPDATE_STATUS.NONE;
        break;
    }
    return result;
  }

  private determineStreamingType(gateway: Gateway): GATEWAY_STREAMING_TYPE {
    if (!gateway || !gateway.streamingDetails || !gateway.streamingDetails.streamingType) {
      return GATEWAY_STREAMING_TYPE.UNKNOWN;
    }
    const streamingType = gateway.streamingDetails.streamingType;

    switch (streamingType) {
      case GATEWAY_STREAMING_TYPE.CONTINUOUS: {
        return GATEWAY_STREAMING_TYPE.CONTINUOUS;
      }
      case GATEWAY_STREAMING_TYPE.NON_CONTINUOUS: {
        return GATEWAY_STREAMING_TYPE.NON_CONTINUOUS;
      }
      default: {
        return GATEWAY_STREAMING_TYPE.UNKNOWN;
      }
    }

  }

  private determineStateNonContinuousStreaming(gateway: Gateway): GATEWAY_STATUS {
    if (!gateway || !gateway.streamingDetails ||
        !gateway.streamingDetails.streamingType ||
        !gateway.streamingDetails.lastUpdateRequestTimestamp) {
      return GATEWAY_STATUS.MISSING;
    }
    const lastUpdateRequestTimestamp = gateway.streamingDetails.lastUpdateRequestTimestamp;
    const now = new Date().getTime();
    const offsetReference = now - this.NON_CONTINUOUS_STREAMING_THRESHOLD_MINUTES * 60 * 1000;
    if (lastUpdateRequestTimestamp < offsetReference) {
      return GATEWAY_STATUS.OFFLINE_OR_UNKNOWN;
    } else {
      return GATEWAY_STATUS.ONLINE;
    }

  }


  private determineStateContinuousStreaming(gateway: Gateway): GATEWAY_STATUS {
    let status;
    if (gateway.connectionStatus.state === CONNECTION_STATE.ONLINE && gateway.sendingData) {
      status = GATEWAY_STATUS.ONLINE;
    } else if (gateway.connectionStatus.state === CONNECTION_STATE.ONLINE && !gateway.sendingData) {
      status = GATEWAY_STATUS.PARTIALLY_DISCONNECTED;
    } else if ([CONNECTION_STATE.OFFLINE, CONNECTION_STATE.UNKNOWN].indexOf(gateway.connectionStatus.state) > -1) {
      status = GATEWAY_STATUS.OFFLINE_OR_UNKNOWN;
    } else {
      status = GATEWAY_STATUS.MISSING;
    }

    return status;
  }
}
